"use client";

import React from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import Wrapper from "@/components/ui/Wrapper";
import { Button } from "@/components/ui/Button";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { HeroSectionFields } from "@/types/acf";

/* ---------------- dots ---------------- */

function SliderDots({
  count,
  selectedIndex,
  onSelect,
  className,
}: {
  count: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
  className?: string;
}) {
  if (count <= 1) return null;

  return (
    <div className={`flex items-center justify-center gap-2 ${className ?? ""}`.trim()}>
      {Array.from({ length: count }).map((_, i) => (
        <button
          key={i}
          type="button"
          aria-label={`Go to slide ${i + 1}`}
          onClick={() => onSelect(i)}
          className={`h-2 w-2 rounded-full transition ${
            i === selectedIndex ? "bg-white" : "bg-white/40 hover:bg-white/70"
          }`}
        />
      ))}
    </div>
  );
}

/* ---------------- component ---------------- */

type ACFMedia = {
  url?: string;
};

export default function HeroSection({ data }: { data: HeroSectionFields }) {

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [isMobile, setIsMobile] = React.useState(false);
  const swiperRef = React.useRef<SwiperType | null>(null);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mql = window.matchMedia("(max-width: 767px)");
    setIsMobile(mql.matches);

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);

    return () => mql.removeEventListener("change", handler);
  }, []);

  const slides = Array.isArray(data?.hero_slides) ? data.hero_slides : [];
  const hasSlides = slides.length > 0;

  if (!data || data.hide || !data.hero_heading) return null;

  const textColor = data.heading_colour === "green" ? "text-fuji-green" : "text-white";
  const MIN_MOBILE_HERO_WIDTH = 900;

  return (
    <div id={data.id} className="m-0 p-0 w-full min-w-0 hero-slider-layout">
      <div className="bg-fuji-grey-dark w-full">
        <div className="relative w-full h-[320px] overflow-hidden">
          <Swiper
            modules={[Autoplay]}
            onSwiper={(s) => {
              swiperRef.current = s;
              setSelectedIndex(s.realIndex ?? 0);
            }}
            onSlideChange={(s) => setSelectedIndex(s.realIndex ?? 0)}
            loop
            grabCursor
            slidesPerView={1}
            autoplay={
              hasSlides
                ? { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }
                : false
            }
            className="h-full"
          >
            {hasSlides ? (
              slides.map((slide, idx) => {
                const desktopSrc = toAbsoluteUrl(slide?.slide_image?.url);
                const mobileSrc = toAbsoluteUrl(slide?.slide_mobile_image?.url);

                const mobileWidth =
                  typeof slide?.slide_mobile_image?.width === "number"
                    ? slide.slide_mobile_image.width
                    : 0;

                const imgSrc =
                  isMobile && mobileSrc && mobileWidth >= MIN_MOBILE_HERO_WIDTH
                    ? mobileSrc
                    : desktopSrc;

                const videoObj = slide?.video as ACFMedia | null | undefined;
                const videoSrc =
                  slide?.image_or_video === "video" && videoObj?.url
                    ? toAbsoluteUrl(videoObj.url)
                    : null;

                const isActive = selectedIndex === idx;

                return (
                  <SwiperSlide key={idx} className="h-full">
                    <div className="relative h-full w-full">
                      {videoSrc && isActive && (
                        <video
                          src={videoSrc}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      )}

                      {slide?.image_or_video === "image" && imgSrc && (
                        <Image
                          src={imgSrc}
                          alt={slide?.slide_image?.alt || `Hero slide ${idx + 1}`}
                          fill
                          className="absolute inset-0 object-cover"
                          priority={idx === 0}
                          sizes="100vw"
                          quality={90}
                        />
                      )}

                      {(videoSrc || imgSrc) && <div className="absolute inset-0 bg-black/40" />}
                    </div>
                  </SwiperSlide>
                );
              })
            ) : (
              <SwiperSlide className="h-full">
                <div className="h-full w-full bg-fuji-grey-dark" />
              </SwiperSlide>
            )}
          </Swiper>

          {/* overlay */}
          <Wrapper className="h-full">
            <div className="pointer-events-none absolute inset-0 z-20 px-lg-to-xl">
              <div className="h-full wrapper">
                <div className="relative h-full flex flex-col items-center justify-center text-center gap-4 max-w-[800px] mx-auto">
                  <h1 className={`!m-0 font-bold text-title-1 ${textColor}`}>
                    {data.hero_heading}
                  </h1>

                  {data.hero_subheading && (
                    <div
                      className="text-title-4 text-white"
                      dangerouslySetInnerHTML={{ __html: data.hero_subheading }}
                    />
                  )}

                  {data.hero_button && (
                    <div className="pointer-events-auto mt-2">
                      <Button
                        as="a"
                        href={toAbsoluteUrl(data.hero_button.url) ?? "#"}
                        target={data.hero_button.target}
                      >
                        {data.hero_button.title}
                      </Button>
                    </div>
                  )}

                  {hasSlides && (
                    <div className="pointer-events-auto absolute bottom-5 left-0 right-0">
                      <SliderDots
                        count={slides.length}
                        selectedIndex={selectedIndex}
                        onSelect={(i) => swiperRef.current?.slideToLoop(i)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Wrapper>
          {/* /overlay */}
        </div>
      </div>
    </div>
  );
}
